import { types } from "mobx-state-tree";
import { NumberModel } from "../../mobxDataModels/numberModel";
import { StringModel } from "../../mobxDataModels/stringModel";
import { ListOfItemsModel } from "../../mobxDataModels/listOfItemsModel";
import { Mk8sDraftProviderGCPAMIModel, Mk8sDraftProviderGCPAMIReadOnlyModel } from "./mk8s.draft.provider.gcp.ami";

export const Mk8sDraftProviderGCPNodePoolsReadonlyModel = types.model({
  name: types.optional(types.string, ""),
  labels: types.array(
    types.model({
      key: types.string,
      value: types.string,
    }),
  ),
  taints: types.array(
    types.model({
      key: types.string,
      value: types.string,
      effect: types.string,
    }),
  ),
  machineType: types.optional(types.string, ""),
  assignPublicIP: types.optional(types.boolean, false),
  preemptible: types.optional(types.boolean, false),
  zone: types.optional(types.string, ""),
  overrideImage: types.optional(Mk8sDraftProviderGCPAMIReadOnlyModel, () =>
    Mk8sDraftProviderGCPAMIReadOnlyModel.create(),
  ),
  bootDiskSize: types.optional(types.number, 20),
  minSize: types.maybe(types.number),
  maxSize: types.maybe(types.number),
  subnet: types.optional(types.string, ""),
  localPersistentDisks: types.optional(types.string, ""),
});

export const Mk8sDraftProviderGCPNodePoolsModel = types
  .model({
    index: types.number,
    status: types.optional(types.enumeration(["default", "added"]), "default"),
    _pool: types.optional(Mk8sDraftProviderGCPNodePoolsReadonlyModel, () =>
      Mk8sDraftProviderGCPNodePoolsReadonlyModel.create(),
    ),
    name: types.optional(StringModel, () =>
      StringModel.create({ label: "Name", validationKey: "nodePoolName", isRequired: true }),
    ),
    labels: types.optional(ListOfItemsModel, () => ListOfItemsModel.create()),
    taints: types.optional(ListOfItemsModel, () => ListOfItemsModel.create()),
    machineType: types.optional(StringModel, () => StringModel.create({ label: "Machine Type", isRequired: true })),
    assignPublicIP: types.optional(types.boolean, false),
    preemptible: types.optional(types.boolean, false),
    zone: types.optional(StringModel, () => StringModel.create({ label: "Zone", isRequired: true })),
    overrideImage: types.optional(Mk8sDraftProviderGCPAMIModel, () => Mk8sDraftProviderGCPAMIModel.create()),
    bootDiskSize: types.optional(StringModel, () =>
      NumberModel.create({ label: "Boot Disk Size", initialValue: 20, isRequired: true }),
    ),
    minSize: types.optional(StringModel, () => NumberModel.create({ label: "Min Size", min: 0, isRequired: true })),
    maxSize: types.optional(StringModel, () => NumberModel.create({ label: "Max Size", min: 0, isRequired: true })),
    subnet: types.optional(StringModel, () => StringModel.create({ label: "Subnet", isRequired: true })),
    localPersistentDisks: types.optional(StringModel, () =>
      StringModel.create({ label: "Local Persistent Disks", isRequired: false }),
    ),
  })
  .actions((self) => ({
    setAssignPublicIp(value: boolean) {
      self.assignPublicIP = value;
    },
    setPreemptible(value: boolean) {
      self.preemptible = value;
    },
    setStatusAdded() {
      self.status = "added";
    },
    reset() {
      self.name.setInitialValue(self._pool.name);
      self.labels = ListOfItemsModel.create({
        _items: self._pool.labels.map((l) => ({ firstValue: l.key, secondValue: l.value })),
      });
      self.taints = ListOfItemsModel.create({
        _items: self._pool.taints.map((t) => ({ firstValue: t.key, secondValue: t.value, thirdValue: t.effect })),
      });
      self.machineType.setInitialValue(self._pool.machineType);
      self.assignPublicIP = self._pool.assignPublicIP;
      self.preemptible = self._pool.preemptible;
      self.zone.setInitialValue(self._pool.zone);

      let imageType: string = "recommended";
      if (self._pool.overrideImage.recommended.length > 0) {
        imageType = "recommended";
      } else if (self._pool.overrideImage.exact.length > 0) {
        imageType = "exact";
      } else if (
        self._pool.overrideImage.familyProject.length > 0 &&
        self._pool.overrideImage.familyFamily.length > 0
      ) {
        imageType = "family";
      }

      self.overrideImage = Mk8sDraftProviderGCPAMIModel.create({
        type: imageType,
        recommended: self._pool.overrideImage.recommended,
        familyProject: self._pool.overrideImage.familyProject,
        familyFamily: self._pool.overrideImage.familyFamily,
        exact: self._pool.overrideImage.exact,
      });

      self.bootDiskSize.setInitialValue(
        String(self._pool.bootDiskSize).length > 0 ? String(self._pool.bootDiskSize) : "",
      );
      self.minSize.setInitialValue(String(self._pool.minSize).length > 0 ? String(self._pool.minSize) : "");
      self.maxSize.setInitialValue(String(self._pool.maxSize).length > 0 ? String(self._pool.maxSize) : "");
      self.subnet.setInitialValue(self._pool.subnet);
      self.localPersistentDisks.setInitialValue(self._pool.localPersistentDisks);
    },
    confirm() {
      self.name.confirm();
      self.labels.confirm();
      self.taints.confirm();
      self.machineType.confirm();
      self.zone.confirm();
      // no override image
      self.bootDiskSize.confirm();
      self.minSize.confirm();
      self.maxSize.confirm();
      self.subnet.confirm();
      self.localPersistentDisks.confirm();

      const _pool: any = {
        name: self.name.value, //
        labels: self.labels.items.map((l) => ({ key: l.firstValue, value: l.secondValue })),
        taints: self.taints.items.map((t) => ({ key: t.firstValue, value: t.secondValue, effect: t.thirdValue })),
        machineType: self.machineType.value,
        assignPublicIP: self.assignPublicIP,
        preemptible: self.preemptible,
        zone: self.zone.value,
        overrideImage: {
          type: self.overrideImage.type,
          exact: self.overrideImage.exact,
          familyProject: self.overrideImage.familyProject,
          familyFamily: self.overrideImage.familyFamily,
          recommended: self.overrideImage.recommended,
        },
        bootDiskSize: Number(self.bootDiskSize.value),
        minSize: String(self.minSize.value).length > 0 ? Number(self.minSize.value) : undefined,
        maxSize: String(self.maxSize.value).length > 0 ? Number(self.maxSize.value) : undefined,
        subnet: self.subnet.value,
        localPersistentDisks: self.localPersistentDisks.value,
      };

      self._pool = Mk8sDraftProviderGCPNodePoolsReadonlyModel.create(_pool);
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.reset();
    },
  }))
  .views((self) => ({
    get isLabelsValid() {
      let res = true;
      if (self.labels.items.some((l) => !l.firstValue || !l.secondValue)) res = false;
      return res;
    },
    get isTaintsValid() {
      let res = true;
      if (self.taints.items.some((t) => !t.firstValue || !t.secondValue || !t.thirdValue)) res = false;
      return res;
    },
  }))
  .views((self) => ({
    get isValid() {
      let res = true;
      if (!self.name.isValid) res = false;
      if (!self.isLabelsValid) res = false;
      if (!self.isTaintsValid) res = false;
      if (!self.machineType.isValid) res = false;
      if (!self.bootDiskSize.isValid) res = false;
      if (!self.minSize.isValid) res = false;
      if (!self.maxSize.isValid) res = false;
      if (!self.overrideImage.isValidForNodePool) res = false;
      if (!self.subnet.isValid) res = false;
      if (!self.zone.isValid) res = false;
      return res;
    },
    get invalidReason() {
      let reason = "";
      if (!self.name.isValid) reason = "name is invalid";
      if (!self.isLabelsValid) reason = "labels not valid";
      if (!self.isTaintsValid) reason = "taints not valid";
      if (!self.machineType.isValid) reason = "machineType is invalid";
      if (!self.bootDiskSize.isValid) reason = "bootDiskSize is invalid";
      if (!self.minSize.isValid) reason = "minSize is invalid";
      if (!self.maxSize.isValid) reason = "maxSize is invalid";
      if (!self.overrideImage.isValidForNodePool) reason = "overrideImage is invalid";
      if (!self.subnet.isValid) reason = "subnet is invalid";
      if (!self.zone.isValid) reason = "zones are required";
      return reason;
    },
    get isDirty() {
      let res = false;
      if (self.name.isDirty) res = true;
      if (self.labels.isDirty) res = true;
      if (self.taints.isDirty) res = true;
      if (self.zone.isDirty) res = true;
      if (self.overrideImage.type !== self._pool.overrideImage.type) {
        res = true;
      }
      if (self.overrideImage.type === "recommended") {
        if (self.overrideImage.recommended !== self._pool.overrideImage.recommended) res = true;
      }
      if (self.overrideImage.type === "exact") {
        if (self.overrideImage.exact !== self._pool.overrideImage.exact) res = true;
      }
      if (self.overrideImage.type === "family") {
        if (self.overrideImage.familyProject !== self._pool.overrideImage.familyProject) res = true;
        if (self.overrideImage.familyFamily !== self._pool.overrideImage.familyFamily) res = true;
      }
      if (self.machineType.isDirty) res = true;
      if (self.assignPublicIP !== self._pool.assignPublicIP) res = true;
      if (self.preemptible !== self._pool.preemptible) res = true;
      if (self.bootDiskSize.isDirty) res = true;
      if (self.minSize.isDirty) res = true;
      if (self.maxSize.isDirty) res = true;
      if (self.subnet.isDirty) res = true;
      if (self.localPersistentDisks.isDirty) res = true;
      return res;
    },
    get asObject() {
      const labels: { [_: string]: string } = {};
      self.labels.items.forEach((l) => {
        labels[l.firstValue] = l.secondValue;
      });

      const obj: any = {
        name: self.name.value,
        labels: labels,
        taints: self.taints.items.map((t) => ({ key: t.firstValue, value: t.secondValue, effect: t.thirdValue })),
        machineType: self.machineType.value,
        assignPublicIP: self.assignPublicIP,
        preemptible: self.preemptible,
        zone: self.zone.value,
        subnet: self.subnet.value,
      };

      if (self.localPersistentDisks.value.length > 0) {
        obj.localPersistentDisks = Number(self.localPersistentDisks.value);
      }

      if (self.overrideImage.type === "recommended" && self.overrideImage.recommended) {
        obj.overrideImage = { recommended: self.overrideImage.recommended };
      }
      if (self.overrideImage.type === "exact" && self.overrideImage.exact) {
        obj.overrideImage = { exact: self.overrideImage.exact };
      }
      if (self.overrideImage.type === "family") {
        obj.overrideImage = {
          family: {
            project: self.overrideImage.familyProject,
            family: self.overrideImage.familyFamily,
          },
        };
      }

      if (self.bootDiskSize.value.length > 0) {
        obj.bootDiskSize = Number(self.bootDiskSize.value);
      }
      if (self.minSize.value.length > 0) {
        obj.minSize = Number(self.minSize.value);
      }
      if (self.maxSize.value.length > 0) {
        obj.maxSize = Number(self.maxSize.value);
      }
      return obj;
    },
  }));
